(() => {
  if (customElements.get('expert-reviews-carousel')) return;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ExpertReviewsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.expert-reviews-carousel__track');
      this.slides = Array.from(this.querySelectorAll('.expert-reviews-carousel__slide'));
      this.controls = this.querySelector('[data-carousel-controls]');
      this.progress = this.querySelector('[data-carousel-progress]');
      this.progressRail = this.querySelector('[data-progress-track]');
      this.progressActive = this.querySelector('[data-progress-active]');
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.status = this.querySelector('[data-carousel-status]');
      this.dialog = this.querySelector('[data-video-dialog]');
      this.videoFrame = this.querySelector('[data-video-frame]');
      this.closeButton = this.querySelector('[data-video-close]');
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.isPointerDown = false;
      this.isProgressDragging = false;
      this.hasDragged = false;
      this.suppressClick = false;
      this.lastTrigger = null;
      this.ownsOverflowLock = false;

      if (!this.track || this.slides.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTrackClick = this.onTrackClick.bind(this);
      this.onProgressPointerDown = this.onProgressPointerDown.bind(this);
      this.onProgressPointerMove = this.onProgressPointerMove.bind(this);
      this.onProgressPointerUp = this.onProgressPointerUp.bind(this);
      this.onProgressKeydown = this.onProgressKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onTrackClick, true);

      this.progress?.addEventListener('pointerdown', this.onProgressPointerDown);
      this.progress?.addEventListener('pointermove', this.onProgressPointerMove);
      this.progress?.addEventListener('pointerup', this.onProgressPointerUp);
      this.progress?.addEventListener('pointercancel', this.onProgressPointerUp);
      this.progress?.addEventListener('keydown', this.onProgressKeydown);

      this.previousButton?.addEventListener('click', () => this.moveBy(-1, true));
      this.nextButton?.addEventListener('click', () => this.moveBy(1, true));

      this.querySelectorAll('[data-video-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openVideo(trigger));
      });

      this.closeButton?.addEventListener('click', () => this.closeVideo());
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('keydown', this.onDialogKeydown);

      // Keep the overlay outside section overflow and stacking contexts used by product templates.
      if (this.dialog) document.body.appendChild(this.dialog);

      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:section:load', this.onSectionLoad);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      }

      requestAnimationFrame(() => this.refresh(false));
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onTrackClick, true);
      this.progress?.removeEventListener('pointerdown', this.onProgressPointerDown);
      this.progress?.removeEventListener('pointermove', this.onProgressPointerMove);
      this.progress?.removeEventListener('pointerup', this.onProgressPointerUp);
      this.progress?.removeEventListener('pointercancel', this.onProgressPointerUp);
      this.progress?.removeEventListener('keydown', this.onProgressKeydown);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('keydown', this.onDialogKeydown);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.resizeObserver?.disconnect();
      this.closeVideo(false);
      this.dialog?.remove();
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateControls(false));
    }

    onResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.refresh(false));
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.moveBy(document.documentElement.dir === 'rtl' ? -direction : direction, true);
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDown = true;
      this.hasDragged = false;
      this.pointerStartX = event.clientX;
      this.scrollStartLeft = this.track.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.isPointerDown) return;
      const distance = event.clientX - this.pointerStartX;
      if (!this.hasDragged && Math.abs(distance) <= 5) return;

      if (!this.hasDragged) {
        this.hasDragged = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }

      this.track.scrollLeft = this.scrollStartLeft - distance;
    }

    onPointerUp(event) {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture?.(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }

      if (this.hasDragged) {
        this.suppressClick = true;
        this.snapToNearest(true);
        window.setTimeout(() => {
          this.suppressClick = false;
          this.hasDragged = false;
        }, 0);
      }
    }

    onTrackClick(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }

    onProgressPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!this.progressRail || !this.progressActive) return;

      event.preventDefault();
      const thumbRect = this.progressActive.getBoundingClientRect();
      const clickedOnThumb = event.clientX >= thumbRect.left && event.clientX <= thumbRect.right;
      this.progressGrabOffset = clickedOnThumb ? event.clientX - thumbRect.left : thumbRect.width / 2;
      this.isProgressDragging = true;
      this.progress.classList.add('is-dragging');
      this.track.classList.add('is-seeking');
      this.progress.setPointerCapture?.(event.pointerId);
      this.seekFromProgressPointer(event);
    }

    onProgressPointerMove(event) {
      if (!this.isProgressDragging) return;
      event.preventDefault();
      this.seekFromProgressPointer(event);
    }

    onProgressPointerUp(event) {
      if (!this.isProgressDragging) return;
      this.isProgressDragging = false;
      this.progress.classList.remove('is-dragging');
      this.track.classList.remove('is-seeking');
      this.progress.releasePointerCapture?.(event.pointerId);
      this.updateControls(true);
    }

    onProgressKeydown(event) {
      if (event.key === 'Home') {
        event.preventDefault();
        this.track.scrollTo({ left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        this.updateControls(true, 0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        this.track.scrollTo({ left: maxScroll, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        this.updateControls(true, this.slides.length - 1);
        return;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.moveBy(document.documentElement.dir === 'rtl' ? -direction : direction, true);
    }

    seekFromProgressPointer(event) {
      const railRect = this.progressRail.getBoundingClientRect();
      const thumbWidth = this.progressActive.getBoundingClientRect().width;
      const travel = Math.max(0, railRect.width - thumbWidth);
      const thumbLeft = Math.max(0, Math.min(event.clientX - railRect.left - this.progressGrabOffset, travel));
      const progress = travel > 0 ? thumbLeft / travel : 0;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollLeft = progress * maxScroll;
      this.updateControls(false);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const selectedSlide = event.target.closest('.expert-reviews-carousel__slide');
      if (!selectedSlide) return;
      const index = this.slides.indexOf(selectedSlide);
      if (index >= 0) this.goTo(index, true, true);
    }

    onSectionLoad(event) {
      if (event.target.contains(this)) this.refresh(false);
    }

    refresh(announce) {
      this.slides = Array.from(this.querySelectorAll('.expert-reviews-carousel__slide'));
      const currentIndex = this.nearestIndex();
      const canScroll = this.track.scrollWidth > this.track.clientWidth + 2;
      this.classList.toggle('is-scrollable', canScroll);
      if (this.controls) this.controls.hidden = !canScroll;
      if (canScroll) this.goTo(currentIndex, false, announce);
      else this.updateControls(announce);
    }

    slideStep() {
      if (this.slides.length < 2) return this.slides[0]?.getBoundingClientRect().width || 0;
      return Math.abs(this.slides[1].offsetLeft - this.slides[0].offsetLeft);
    }

    slideLeft(slide) {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 0;
      return slide.offsetLeft - firstSlide.offsetLeft;
    }

    nearestIndex() {
      const currentLeft = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(currentLeft - this.slideLeft(slide));
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    goTo(index, smooth, announce) {
      const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const targetLeft = Math.min(this.slideLeft(this.slides[targetIndex]), maxScroll);

      this.track.scrollTo({
        left: targetLeft,
        behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto'
      });
      this.updateControls(announce, targetIndex);
    }

    moveBy(direction, announce) {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (direction > 0 && this.track.scrollLeft >= maxScroll - 2) return;
      if (direction < 0 && this.track.scrollLeft <= 2) return;
      this.goTo(this.nearestIndex() + direction, true, announce);
    }

    snapToNearest(announce) {
      this.goTo(this.nearestIndex(), true, announce);
    }

    updateControls(announce, forcedIndex) {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const scrollLeft = Math.max(0, Math.min(this.track.scrollLeft, maxScroll));
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const index = forcedIndex ?? this.nearestIndex();

      if (this.previousButton) this.previousButton.disabled = scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = scrollLeft >= maxScroll - 2;

      if (this.progress && this.progressRail && this.progressActive && this.slides.length > 0) {
        const slideWidth = this.slides[0].getBoundingClientRect().width;
        const step = this.slideStep() || slideWidth;
        const gap = Math.max(0, step - slideWidth);
        const visibleCards = Math.min(this.slides.length, (this.track.clientWidth + gap) / Math.max(step, 1));
        const thumbRatio = Math.max(0, Math.min(1, visibleCards / this.slides.length));
        const railWidth = this.progressRail.clientWidth;
        const thumbWidth = railWidth * thumbRatio;
        const travel = Math.max(0, railWidth - thumbWidth);

        this.progressActive.style.width = `${thumbRatio * 100}%`;
        this.progressActive.style.transform = `translateX(${progress * travel}px)`;
        this.progress.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
        this.progress.setAttribute('aria-valuetext', `Starting at review ${index + 1} of ${this.slides.length}`);
      }

      if (announce && this.status) {
        this.status.textContent = `Starting at review ${index + 1} of ${this.slides.length}`;
      }
    }

    openVideo(trigger) {
      if (!this.dialog || !this.videoFrame) return;

      const openCarousel = document.querySelector('expert-reviews-carousel[data-video-open]');
      const inheritedLockOwnership = openCarousel ? openCarousel.ownsOverflowLock : !document.body.classList.contains('overflow-hidden');
      if (openCarousel && openCarousel !== this && typeof openCarousel.closeVideo === 'function') {
        openCarousel.ownsOverflowLock = false;
        openCarousel.closeVideo(false);
      }

      const provider = trigger.dataset.videoProvider;
      const videoId = trigger.dataset.videoId;
      const videoTitle = trigger.dataset.videoTitle || 'Expert review video';
      let source = '';

      if (provider === 'youtube') {
        source = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&playsinline=1`;
      } else if (provider === 'vimeo') {
        source = `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1`;
      }

      if (!source) return;

      this.destroyIframe();
      const iframe = document.createElement('iframe');
      iframe.src = source;
      iframe.title = videoTitle;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.videoFrame.appendChild(iframe);

      this.lastTrigger = trigger;
      this.ownsOverflowLock = inheritedLockOwnership;
      this.setAttribute('data-video-open', '');
      document.body.classList.add('overflow-hidden');
      this.dialog.hidden = false;
      requestAnimationFrame(() => this.closeButton?.focus());
    }

    closeVideo(returnFocus = true) {
      if (!this.dialog || this.dialog.hidden) {
        this.destroyIframe();
        this.removeAttribute('data-video-open');
        return;
      }
      this.returnFocusOnClose = returnFocus;
      this.dialog.hidden = true;
      this.onDialogClose();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.closeVideo();
    }

    onDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeVideo();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (focusable.length === 0) {
        event.preventDefault();
        this.closeButton?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onDialogClose() {
      this.destroyIframe();
      this.removeAttribute('data-video-open');
      if (this.ownsOverflowLock) document.body.classList.remove('overflow-hidden');
      this.ownsOverflowLock = false;
      if (this.returnFocusOnClose !== false) this.lastTrigger?.focus();
      this.returnFocusOnClose = true;
    }

    destroyIframe() {
      if (this.videoFrame) this.videoFrame.replaceChildren();
    }
  }

  customElements.define('expert-reviews-carousel', ExpertReviewsCarousel);
})();
